import { Button, ButtonSize, ButtonStyle } from "components/buttons/button/button";
import { FormSelect } from "components/form/form-select/form-select";
import { SearchTextInput } from "components/form/inputs/text-inputs/search-text-input/search-text-input";
import { JSX, useState } from "react";
import { EnumUtils } from "utilities/enumerations/enum-utils";
import { NumberUtils } from "utilities/number-utils";
import { t } from "utilities/localization/t";
import { ActiveStatus, ActiveStatusStatusDisplayNames } from "models/enumerations/active-status/active-status";
import { Language, LanguageDisplayNames } from "models/enumerations/languages/language";
import { Topic, TopicDisplayNames } from "models/enumerations/courses/topic";
import { AudienceType, AudienceTypeDisplayNames } from "models/enumerations/audiences/audience-type";
import { useCourseSearch } from "utilities/contexts/search/course/course-search-context";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------
interface AdminCourseFilterComponentProps {
}
// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------
const CSS_CLASS_NAME = "admin-Course-filter-component";
const DEBOUNCE_TIME = 2000;
// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------
const AdminCourseFilterComponent: React.FC<AdminCourseFilterComponentProps> = ({}): JSX.Element => {
    const { initialCriteria: criteria, setCriteria } = useCourseSearch();

    const [searchText, setSearchText] = useState<string>(criteria?.searchText ?? "");
    const [audienceType, setAudienceType] = useState<AudienceType | undefined>( criteria?.audienceType);
    const [topic, setTopic] = useState<Topic | undefined>(criteria?.topic);
    const [language, setLanguage] = useState<Language | undefined>(criteria?.language);
    const [status, setStatus] = useState<ActiveStatus>(criteria?.status ?? ActiveStatus.Active);


    const handleSearchTriggered = (searchText : string) => {
        setCriteria((prev) => ({ ...prev, 
            searchText: searchText, 
            currentPage: 1 }));
    };

    const handleSearchTextChange = (searchText: string): void => {
        setSearchText(searchText);
    };

    const handleFilterByAudienceTypeChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ): void => {
        const audienceTypeIndex = NumberUtils.parseInt(event.target.value) ?? undefined;
        if (audienceTypeIndex === undefined) {
            setAudienceType(undefined);
            setCriteria((prev) => ({ ...prev, audienceType: undefined, currentPage: 1 }));
            return;
        }

        const newAudienceType =
            isNaN(audienceTypeIndex) || audienceTypeIndex < 0 ? undefined : audienceTypeIndex;
        setAudienceType(newAudienceType);
        setCriteria((prev) => ({ ...prev, audienceType: newAudienceType, currentPage: 1 }));
    };

    const handleFilterByLanguageChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const languageIndex = NumberUtils.parseInt(event.target.value) ?? undefined;
        setLanguage(languageIndex);
        setCriteria((prev) => ({ ...prev, language: languageIndex , currentPage: 1 }));
    };

    const handleFilterByTopicChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const topic = NumberUtils.parseInt(event.target.value) ?? undefined;
        setTopic(topic);
        setCriteria((prev) => ({ ...prev, topic, currentPage: 1 }));
    };

    const handleFilterByStatusChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const activeStatusIndex = NumberUtils.parseInt(event.target.value) ?? 0;
        const newStatus =
            isNaN(activeStatusIndex) || activeStatusIndex < 0 ? ActiveStatus.Active : activeStatusIndex;
        setStatus(newStatus);
        setCriteria((prev) => ({ ...prev, status: newStatus, currentPage: 1 }));
    };

    const handleClearAll = () => {
        setLanguage(undefined);
        setTopic(undefined);
        setAudienceType(undefined);
        setStatus(ActiveStatus.Active);
        setSearchText("");
        
        setCriteria((prev) => ({
            ...prev,
            language: undefined,
            topic: undefined,
            audienceType: undefined,
            status: ActiveStatus.Active,
            searchText: "",
            currentPage: 1,
        }));
    };
    
    return (
        <div className={`${CSS_CLASS_NAME}__sidebar`}>
            <SearchTextInput
                dataTestId="admin-course-list-page:course-search:input"
                debounce={DEBOUNCE_TIME}
                onSearchTextInputChange={handleSearchTextChange}
                onSearchTriggered={handleSearchTriggered}
                id={"courseSearch"}
                placeholder={t("searchByCourseNameOrID")}
                searchTextInputValue={searchText}
            />
            <FormSelect
                ariaLabelledBy={t("filterByType")}
                dataTestId="admin-course-list-page:filter-by-type:select"
                formFieldName={t("filterByType")}
                id="filterByType"
                label=""
                placeholder={t("filterByType")}
                onChange={handleFilterByAudienceTypeChange}
                options={[
                    {text: "", value: ""},
                    ...EnumUtils.numericEnumToSelectOptions(
                        AudienceType,
                        AudienceTypeDisplayNames
                    )
                ]}
                value={audienceType?.toString()}
            />
            <FormSelect
                ariaLabelledBy={t("filterByLanguage")}
                dataTestId="admin-course-list-page:filter-by-language:select"
                formFieldName={t("filterByLanguage")}
                id="instructorFilterByLanguage"
                label=""
                placeholder={t("filterByLanguage")}
                onChange={handleFilterByLanguageChange}
                options={[
                    {text: "", value: ""},      
                    ...EnumUtils.numericEnumToSelectOptions(
                    Language,
                    LanguageDisplayNames)
                ]}
                value={language?.toString()}
            />
            <FormSelect
                ariaLabelledBy={t("filterByTopic")}
                dataTestId="admin-course-list-page:filter-by-topic:select"
                formFieldName={t("filterByTopic")}
                id="instructorFilterByTopic"
                label=""
                placeholder={t("filterByTopic")}
                onChange={handleFilterByTopicChange}
                options={[
                    {text: "", value: ""},
                    ...EnumUtils.numericEnumToSelectOptions(
                    Topic,
                    TopicDisplayNames
                )]}
                value={topic?.toString()}
            />
            <FormSelect
                ariaLabelledBy={t("filterByStatus")}
                dataTestId="admin-course-list-page:filter-by-status:select"
                formFieldName={t("filterByStatus")}
                id="courseFilterByStatus"
                label=""
                placeholder={t("status")}
                onChange={handleFilterByStatusChange}
                options={EnumUtils.numericEnumToSelectOptions(
                    ActiveStatus,
                    ActiveStatusStatusDisplayNames
                )}
                value={status?.toString()}
            />
            
            <Button
                dataTestId="admin-course-list-page:clear-all:btn"
                onClick={handleClearAll}
                size={ButtonSize.Medium}
                style={ButtonStyle.Secondary}
                text={t("clearAllFilters")}
            />
        </div>
    );
}

export default AdminCourseFilterComponent;